import { useEffect, useState } from "react";
import { Loader2, Trash2, UserPlus } from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const select = "h-11 w-full rounded-md border border-ink-600 bg-ink-800 px-3 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-gold";

const Toggle = ({ testId, on, onClick, title, hint }) => (
  <button type="button" data-testid={testId} onClick={onClick}
    className={`flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-colors ${
      on ? "border-gold/50 bg-gold/10" : "border-ink-600 bg-ink-800 hover:border-gold/30"}`}>
    <span className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border ${on ? "border-gold bg-gold" : "border-slate-500"}`} />
    <span className="min-w-0">
      <span className="block text-sm text-slate-100">{title}</span>
      <span className="block text-xs text-slate-400">{hint}</span>
    </span>
  </button>
);

/** How passengers pay — the company rule, plus exceptions for named clients. */
export const PaymentPolicyPanel = ({ onCompany }) => {
  const [state, setState] = useState(null);
  const [form, setForm] = useState({ mode: "percentage", deposit_percent: 25, deposit_amount: 0, per_client: false, pay_before_approval: true });
  const [terms, setTerms] = useState({ email: "", name: "", mode: "full", deposit_percent: 25, deposit_amount: 0 });
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  const apply = (data) => {
    setState(data);
    setForm({ ...data.policy });
    if (data.company && onCompany) onCompany(data.company);
  };

  useEffect(() => {
    api.get("/company/payment-policy").then(({ data }) => apply(data)).catch((e) => setError(errMsg(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (key, call, done) => {
    setBusy(key); setError(""); setMsg("");
    try {
      const { data } = await call();
      apply(data);
      setMsg(done);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  if (!state) {
    return (
      <div data-testid="payment-policy-loading" className="rounded-xl border border-ink-600 bg-ink-800/60 p-6">
        {error ? <p data-testid="payment-policy-error" className="text-sm text-red-400">{error}</p>
          : <Loader2 className="h-5 w-5 animate-spin text-gold" />}
      </div>
    );
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="payment-policy-panel" className="rounded-xl border border-ink-600 bg-ink-800/60 p-6 md:p-8">
      <h2 className="text-lg font-medium text-slate-100">How passengers pay</h2>
      <p className="mt-2 text-sm text-slate-400">
        This decides what a passenger is asked for when they book — on your booking page, the rider
        app and the client portal. It replaces the old per-vehicle deposit.
      </p>

      <div className="mt-6 space-y-5">
        <div>
          <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">Your rule</label>
          <select data-testid="policy-mode" value={form.mode} onChange={(e) => set("mode", e.target.value)} className={select}>
            {state.modes.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        </div>

        {form.mode === "percentage" && (
          <div>
            <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">Deposit percentage</label>
            <Input data-testid="policy-percent" type="number" min="1" max="100" value={form.deposit_percent}
              onChange={(e) => set("deposit_percent", e.target.value)}
              className="h-11 border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" />
          </div>
        )}
        {form.mode === "fixed_deposit" && (
          <div>
            <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">Deposit amount</label>
            <Input data-testid="policy-amount" type="number" step="0.01" min="1" value={form.deposit_amount}
              onChange={(e) => set("deposit_amount", e.target.value)}
              className="h-11 border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" />
          </div>
        )}

        <Toggle testId="policy-pay-before" on={form.pay_before_approval}
          onClick={() => set("pay_before_approval", !form.pay_before_approval)}
          title={form.pay_before_approval ? "They can pay as soon as they request a trip" : "They pay only after you confirm the trip"}
          hint={form.pay_before_approval
            ? "The card page opens with the request. Tap to make them wait for your confirmation instead."
            : "The request arrives as an estimate; the payment link goes out when you create the manifest."} />

        <Toggle testId="policy-per-client" on={form.per_client}
          onClick={() => set("per_client", !form.per_client)}
          title={form.per_client ? "Terms are set per client" : "Every client is treated the same"}
          hint={form.per_client
            ? "The rule above is the default; the clients below have their own terms."
            : "Tap to give named clients their own terms — a hotel on account, a walk-in paying in full."} />

        <Toggle testId="policy-auto-charge" on={form.auto_charge_balance}
          onClick={() => set("auto_charge_balance", !form.auto_charge_balance)}
          title={form.auto_charge_balance
            ? `The balance is charged automatically ${state.grace_minutes} minutes after the trip`
            : "Balances are collected by hand"}
          hint={form.auto_charge_balance
            ? `Passengers who tick "keep my card on file" are charged what is left ${state.grace_minutes} minutes after the chauffeur finishes — time enough for you to add waiting time. A declined card emails them a link and lands in Requests.`
            : "Tap to settle the remaining fare on the card the passenger left on file, so nobody has to chase it."} />

        <p data-testid="policy-summary" className="rounded-lg border border-gold/30 bg-gold/5 p-4 text-sm text-slate-200">
          {state.summary}
        </p>
        {form.auto_charge_balance && !state.payments_ready && (
          <p data-testid="policy-auto-charge-warning" className="text-sm text-amber-400">
            Connect your Stripe account below before this can run — until then balances stay manual.
          </p>
        )}
        {form.auto_charge_balance && state.processor !== "stripe" && (
          <p data-testid="policy-auto-charge-processor" className="text-sm text-amber-400">
            Keeping a card on file works on Stripe only right now. On Square the passenger still gets
            an emailed payment link.
          </p>
        )}

        {msg && <p data-testid="policy-saved" className="text-sm text-emerald-400">{msg}</p>}
        {error && <p data-testid="policy-error" className="text-sm text-red-400">{error}</p>}

        <Button data-testid="policy-save" disabled={busy !== ""}
          onClick={() => run("policy", () => api.put("/company/payment-policy", {
            mode: form.mode,
            deposit_percent: Number(form.deposit_percent || 0),
            deposit_amount: Number(form.deposit_amount || 0),
            per_client: !!form.per_client,
            pay_before_approval: !!form.pay_before_approval,
            auto_charge_balance: !!form.auto_charge_balance,
          }), "Saved — new requests follow this rule.")}
          className="h-11 bg-gold px-8 text-ink-900 hover:bg-gold-light">
          {busy === "policy" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save payment rule"}
        </Button>

        {form.per_client && (
          <div data-testid="client-terms" className="space-y-4 border-t border-ink-600 pt-6">
            <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Terms per client</p>
            {state.clients.length === 0 && (
              <p data-testid="client-terms-empty" className="text-sm text-slate-500">
                Nobody has their own terms yet — everyone follows the rule above.
              </p>
            )}
            {state.clients.map((c) => (
              <div key={c.email} data-testid={`terms-row-${c.email}`}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-ink-600 bg-ink-800 p-4">
                <div className="min-w-0">
                  <p className="truncate text-sm text-slate-100">{c.name || c.email}</p>
                  <p className="truncate font-mono text-[11px] text-slate-500">
                    {c.email} · {(state.modes.find((m) => m.key === c.mode) || {}).label}
                    {c.mode === "percentage" ? ` · ${c.deposit_percent}%` : ""}
                    {c.mode === "fixed_deposit" ? ` · ${money(c.deposit_amount)}` : ""}
                  </p>
                </div>
                <Button data-testid={`terms-remove-${c.email}`} size="sm" variant="outline" disabled={busy !== ""}
                  onClick={() => run(`rm-${c.email}`, () => api.delete(`/company/client-terms/${encodeURIComponent(c.email)}`),
                    `${c.email} is back on your standard rule.`)}
                  className="border-red-500/40 bg-transparent text-red-300 hover:bg-red-500/10">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input data-testid="terms-email" type="email" placeholder="client@company.com" value={terms.email}
                onChange={(e) => setTerms({ ...terms, email: e.target.value })}
                className="h-11 border-ink-600 bg-ink-800 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold" />
              <Input data-testid="terms-name" placeholder="Name (optional)" value={terms.name}
                onChange={(e) => setTerms({ ...terms, name: e.target.value })}
                className="h-11 border-ink-600 bg-ink-800 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold" />
              <select data-testid="terms-mode" value={terms.mode} onChange={(e) => setTerms({ ...terms, mode: e.target.value })} className={select}>
                {state.modes.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
              {terms.mode === "percentage" && (
                <Input data-testid="terms-percent" type="number" min="1" max="100" value={terms.deposit_percent}
                  onChange={(e) => setTerms({ ...terms, deposit_percent: e.target.value })}
                  className="h-11 border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" />
              )}
              {terms.mode === "fixed_deposit" && (
                <Input data-testid="terms-amount" type="number" step="0.01" min="1" value={terms.deposit_amount}
                  onChange={(e) => setTerms({ ...terms, deposit_amount: e.target.value })}
                  className="h-11 border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" />
              )}
            </div>
            <Button data-testid="terms-save" variant="outline" disabled={busy !== "" || !terms.email}
              onClick={() => run("terms", async () => {
                // Saving a client's terms is meaningless while 'per client' is still off, so the
                // toggle is committed first rather than silently reverting under them.
                if (!state.policy.per_client) {
                  await api.put("/company/payment-policy", {
                    mode: form.mode,
                    deposit_percent: Number(form.deposit_percent || 0),
                    deposit_amount: Number(form.deposit_amount || 0),
                    per_client: true,
                    pay_before_approval: !!form.pay_before_approval,
                    auto_charge_balance: !!form.auto_charge_balance,
                  });
                }
                return api.put("/company/client-terms", {
                  email: terms.email.trim(), name: terms.name.trim(), mode: terms.mode,
                  deposit_percent: Number(terms.deposit_percent || 0),
                  deposit_amount: Number(terms.deposit_amount || 0),
                });
              }, `${terms.email.trim()} now has their own terms.`)}
              className="border-gold/40 bg-transparent text-gold hover:bg-gold/10">
              {busy === "terms" ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><UserPlus className="mr-2 h-4 w-4" /> Save this client's terms</>)}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
